import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY } from '../util/constant';
import { Table, Modal, Button, Form, FloatingLabel, Row, Col, Accordion } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { EditIcon } from './icons';
import 'react-toastify/dist/ReactToastify.css';
import PaginationInPage from './Pagination';
import { roles } from '../util/helper';
import useDebounce from './useDebounce';
import { GrDocumentExcel } from 'react-icons/gr';
import LocationTypeAndLocation from '../components/User/LocationTypeAndLocation';
import Breadcrumb from '../components/Breadcrumb';
import { useSelector } from 'react-redux';

const Users = () => {
  const formFields = {
    name: '',
    email: '',
    phone: '',
    password: '',
    role: {},
    collectionCenterId: '',
    locationType: '',
    locationId: [],
    companyId: 1,
  };
  const [databaseRoles, setDatabaseRoles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [userList, setUserList] = useState([]);
  const [userForm, setUserForm] = useState(formFields);
  const [userFormDatabase, setUserFormDatabase] = useState(formFields);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  // const [idToDeleted, setIdToDeleted] = useState("");
  // const [deleteModel, setDeleteModel] = useState(false)
  const [collectionCenterList, setCollectionCenterList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [company, setCompany] = useState('');
  const [locationList, setLocationList] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [query, setQuery] = useState('');
  const [userRole, setUserRole] = useState('');
  const clickToDownload = useRef(null);

  const permissions = useSelector((state) => state.permission.permissions);

  const handleShow = () => {
    setShowConfirmModel(true);
    setUserForm(formFields);
  };

  const handleClose = () => {
    setShowConfirmModel(false);
  };

  const handleEdit = (e, id) => {
    e.preventDefault();
    try {
      get('/users/?id=' + id).then((user) => {
        if (user?.data?.data?.length) {
          const userData = user.data.data[0];
          setUserForm({ ...userData });
          const filteredLocation = locationList.filter((item) => item?.companyId === parseInt(userData?.companyId));
          setFilteredLocations([].concat(filteredLocation));
          setDatabaseRoles(Object.keys(userData.role));
          setShowConfirmModel(true);
        } else {
          toast.error('Invalid user id');
        }
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.rows?.message || 'Something went wrong');
    }
  };

  const handleChange = (event) => {
    event.preventDefault();
    let name = event.target.name;
    let value = event.target.value;
    let _userForm = { ...userForm, [name]: value };
    if (name === 'role') {
      _userForm = { ..._userForm, locationType: '', locationId: [], collectionCenterId: '' };
    }
    setUserForm(_userForm);
    if (name === 'locationType' || name === 'companyId') {
      const factoryLocations = locationList.filter(
        (item) => item.type === value && item.isActive === true && item?.companyId === parseInt(userForm?.companyId)
      );
      setFilteredLocations([].concat(factoryLocations));
    }
  };

  const locationTypeHandler = (locationType, module) => {
    let role = { ...userForm.role };
    role[module] = { ...role[module], locationType: locationType };
    setUserForm((prevState) => {
      return { ...prevState, role };
    });
  };

  const locationSelectHandler = (ids, module) => {
    let role = { ...userForm.role };
    role[module] = { ...role[module], locations: ids };
    setUserForm((prevState) => {
      return { ...prevState, role };
    });
  };

  const upsertRole = (e) => {
    const { name, checked } = e.target;
    let role = { ...userForm.role };
    if (checked) {
      role = { ...role, [name]: {} };
      switch (name) {
        case 'store-manager':
          role['store-manager'] = { collectionCenterId: '' };
          break;
        case 'fixed-asset-manager':
        case 'coupon-manager':
        case 'vehicle-manager':
        case 'machine-manager':
        case 'festival-manager':
          role[name] = { locationType: '', locations: [] };
          break;
        default:
          role[name] = {};
      }
    } else {
      delete role[name];
    }
    setUserForm((prevState) => {
      return { ...prevState, role };
    });
  };

  const handleCollectionCenterChange = (e) => {
    const { value } = e.target;
    let role = { ...userForm.role };
    role['store-manager'] = { collectionCenterId: value };
    setUserForm((prevState) => {
      return { ...prevState, role };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if ('id' in userForm) {
      try {
        await put(userForm, '/users/' + userForm.id);
        toast.success('User updated successfully');
        setShowConfirmModel(false);
        getUsers();
      } catch (err) {
        toast.error(err.response.data.message || err.response.data.data.message || err.message, { autoClose: 3000 });
      }
    } else {
      try {
        await post(userForm, '/users');
        toast.success('User added successfully');
        setShowConfirmModel(false);
        getUsers();
      } catch (err) {
        toast.error(err.response.data.message || err.response.data.data.message || err.message, { autoClose: 2000 });
      }
    }
  };

  function getUsers() {
    try {
      get(`/users/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&query=${query}&role=${userRole}&companyId=${company}`).then(
        (response) => {
          setUserList(response.data.data);
          setTotalRecords(response.data.count ?? 0);
        }
      );
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  function getCollectionDropdown() {
    get('/collection-centers').then((response) => {
      setCollectionCenterList(response.data.rows);
    });
  }

  function getCompanyDropdown() {
    try {
      get('/companies').then((response) => {
        setCompanyList(response.data.rows);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getUsers();
      }
    },
    [query, userRole, company],
    DEBOUNCE_DELAY
  );

  const setActiveStatus = (e, userId) => {
    e.preventDefault();
    try {
      put({ isActive: e.target.value }, '/users/' + userId).then((response) => {
        toast.success('Activity Status Updated');
        getUsers();
      });
    } catch (e) {
      console.log(e.message);
    }
  };

  function excelReport() {
    try {
      get(`/users/?conditionType=download`).then((response) => {
        clickToDownload.current.href = `${process.env.REACT_APP_BASE_URL}/file/download?filePath=${response.data.filePath}`;
        setTimeout(() => {
          clickToDownload.current.click();
        }, 500);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  function getLocations() {
    try {
      get(`/branches/`).then((response) => {
        setLocationList(response.data.rows);
        setFilteredLocations(response.data.rows);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  useEffect(() => {
    getCollectionDropdown();
    getLocations();
    getCompanyDropdown();
  }, []);

  useEffect(() => {
    getUsers();
  }, [currentPage]);

  //const options = filteredLocations.map((item) => ({ value: item?.id, label: item?.name }));

  const companyChangeHandler = (e) => {
    const { value } = e.target;
    setUserForm((state) => {
      return { ...state, companyId: value };
    });
    // It should filter the locations
    const companyLocations = locationList.filter((item) => item?.companyId === +value);
    setFilteredLocations([].concat(companyLocations));
  };

  const getUniqueLocations = (inputString) => {
    const words = inputString.split(',').map((item) => item.trim());
    const uniqueWords = Array.from(new Set(words));
    return uniqueWords.join(', ');
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Breadcrumb
        navigation={[
          { link: '#', title: 'Settings' },
          { link: '#', title: 'Users' },
        ]}
      />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Users | {totalRecords ?? 0}
          </h4>
          <div className="card-header-actions">
            <Form.Select
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="input"
              style={{ width: '200px', marginRight: '20px' }}
            >
              <option value="">Select Company</option>
              {companyList.map((company) => {
                return (
                  <option key={company?.id} value={company?.id}>
                    {company?.name}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              className="input"
              style={{ width: '200px', marginRight: '20px' }}
            >
              <option value="">Select Role</option>
              {Object.keys(roles).map((role) => (
                <option value={role}>{roles[role]}</option>
              ))}
            </Form.Select>
            <Form.Control
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder="Search by name, email, phone"
              style={{ width: '250px', marginRight: '20px' }}
            />
            <GrDocumentExcel title="Download users" className="react-icons green" onClick={excelReport} />
            <a ref={clickToDownload} href="/" target="_blank" className="hiddenItem">
              HiddenClick
            </a>
            {permissions?.includes('add_user') && (
              <button type="button" className="btn app-btn-default dark-color " onClick={handleShow}>
                Add User
              </button>
            )}
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email Address</th>
                    <th>Phone</th>
                    <th>Role/s</th>
                    <th>Collection Center</th>
                    <th>Location</th>
                    <th>Company</th>
                    <th>IsActive</th>
                    {permissions.includes('edit_user') && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {userList.map((item, index) => {
                    return (
                      <tr key={item?.id}>
                        <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                        <td>{item?.name}</td>
                        <td>{item?.email}</td>
                        <td>{item?.phone}</td>
                        <td>
                          {Object.keys(item.role)
                            .map((role) => roles[role])
                            .join(', ')}
                        </td>
                        <td>{item?.collectionCenterName}</td>
                        <td>{getUniqueLocations(item?.locationNames) || '-'}</td>
                        <td>{item?.companyName}</td>
                        <td>
                          <select
                            disabled={!permissions.includes('update_user')}
                            value={item?.isActive}
                            onChange={(e) => setActiveStatus(e, item?.id)}
                          >
                            <option value={true}>Active</option>
                            <option value={false}>inActive</option>
                          </select>
                        </td>
                        {permissions.includes('edit_user') && (
                          <td className="actions">
                            <span onClick={(e) => handleEdit(e, item?.id)} className="edit-icon">
                              <EditIcon style={{ color: 'teal' }} />
                            </span>
                          </td>
                        )}
                        {/* <Link onClick={(e) => handleConfirmDeleteModal(e, item?.id)} className="delete" to="/"><DeleteIcon /></Link> */}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal size="lg" show={showConfirmModel} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>{'id' in userForm ? 'Edit User' : 'Add User'}</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
        </Modal.Header>
        <Modal.Body>
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Row>
              <Col xs={6}>
                <FloatingLabel label="Name" className="mb-3">
                  <Form.Control
                    className="input"
                    name="name"
                    onChange={handleChange}
                    value={userForm.name}
                    type="text"
                    placeholder="Name"
                    required
                    autoComplete="false"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel label="Email Address" className="mb-3">
                  <Form.Control
                    className="input"
                    onChange={handleChange}
                    value={userForm.email}
                    name="email"
                    type="email"
                    placeholder="Email Address"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Phone No." className="mb-3">
                  <Form.Control
                    className="input"
                    autoComplete="false"
                    onChange={handleChange}
                    value={userForm.phone}
                    name="phone"
                    type="text"
                    placeholder="Phone No."
                    required
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Password" className="mb-3">
                  <Form.Control
                    required
                    className="input"
                    onChange={handleChange}
                    value={userForm.password}
                    autoComplete="off"
                    name="password"
                    type="password"
                    placeholder="Password"
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4}>
                <FloatingLabel label="Company" className="mb-3">
                  <select
                    disabled={'id' in userForm}
                    required
                    name="companyId"
                    value={userForm.companyId}
                    onChange={companyChangeHandler}
                    aria-label="Floating label select example"
                    className="form-select"
                  >
                    <option value="">Select Company</option>
                    {companyList.map((item) => (
                      <option key={item?.id} value={item.id}>
                        {item?.name}
                      </option>
                    ))}
                  </select>
                </FloatingLabel>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div key={`inline-checkbox`} className="mb-3">
                  {Object.keys(roles).map((role) => (
                    <Form.Check
                      key={role}
                      inline
                      onChange={upsertRole}
                      label={roles[role]}
                      name={role}
                      type="checkbox"
                      checked={Object.keys(userForm.role).includes(role)}
                      id={`inline-checkbox-${role}`}
                    />
                  ))}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Accordion defaultActiveKey={[]} alwaysOpen>
                  {/* For Store Manager */}
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Cash Collector</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col xs={12}>
                          <FloatingLabel label="Collection Center" className="mb-3">
                            <select
                              required={'store-manager' in userForm.role}
                              disabled={!('store-manager' in userForm.role)}
                              name="collectionCenterId"
                              value={userForm.role['store-manager']?.collectionCenterId}
                              onChange={handleCollectionCenterChange}
                              aria-label="Floating label select example"
                              className="form-select"
                            >
                              <option value="">Select Collection Center</option>
                              {collectionCenterList
                                .filter((item) => item.companyId === parseInt(userForm.companyId))
                                .map((item) => {
                                  return (
                                    <option key={item?.id} value={item.id}>
                                      {item?.name}
                                    </option>
                                  );
                                })}
                            </select>
                          </FloatingLabel>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* For Fixed Asset Manager */}
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>Fixed Asset Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="fixed-asset-manager"
                        data={userForm.role['fixed-asset-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('fixed-asset-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={true}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Vehile Manager */}
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>Vehicle Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="vehicle-manager"
                        data={userForm.role['vehicle-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('vehicle-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={true}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Machine Manager */}
                  <Accordion.Item eventKey="3">
                    <Accordion.Header>Machine Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="machine-manager"
                        data={userForm.role['machine-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('machine-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={true}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Coupon Manager */}
                  <Accordion.Item eventKey="4">
                    <Accordion.Header>Coupon Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="coupon-manager"
                        data={userForm.role['coupon-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('coupon-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={false}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Festival Manager */}
                  <Accordion.Item eventKey="5">
                    <Accordion.Header>Festival Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="festival-manager"
                        data={userForm.role['festival-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('festival-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={false}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Inventory Manager */}
                  <Accordion.Item eventKey="8">
                    <Accordion.Header>Inventory Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="inventory-manager"
                        data={userForm.role['inventory-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('inventory-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={false}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* For JW Collector */}
                  <Accordion.Item eventKey="7">
                    <Accordion.Header>JW Collector</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="jw-collector"
                        data={userForm.role['jw-collector']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('jw-collector') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={true}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                  {/* Cake Manager */}
                  <Accordion.Item eventKey="6">
                    <Accordion.Header>Cake Manager</Accordion.Header>
                    <Accordion.Body>
                      <LocationTypeAndLocation
                        moduleName="cake-manager"
                        data={userForm.role['cake-manager']}
                        optionTypes={['Outlet', 'Office', 'Factory']}
                        disable={!Object.keys(userForm.role).includes('cake-manager') || userForm.companyId === ''}
                        onLocationTypeChange={locationTypeHandler}
                        onLocationsSelect={locationSelectHandler}
                        locations={filteredLocations}
                        isMulti={false}
                        databaseRoles={databaseRoles}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
            <div className="container text-center">
              <Button type="submit" className="add-request-btn mt-4 mb-3 justify-content-center">
                {'id' in userForm ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Users;
